import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import * as Styled from "./styles";

type DrawerSide = "left" | "right";

interface DrawerMenuRenderProps {
  closeDrawer: () => void;
}

export interface DrawerMenuProps {
  open: boolean;
  onClose: () => void;
  anchor?: DrawerSide;
  width?: number | string;
  showBackdrop?: boolean;
  closeOnBackdropClick?: boolean;
  transitionDuration?: number;
  children: ReactNode | ((props: DrawerMenuRenderProps) => ReactNode);
  className?: string;
  container?: Element | DocumentFragment | null;
}

const DEFAULT_TRANSITION_MS = 280;
const DEFAULT_WIDTH = 525;

const DrawerMenu: React.FC<DrawerMenuProps> = ({
  open,
  onClose,
  anchor = "right",
  width = DEFAULT_WIDTH,
  showBackdrop = true,
  closeOnBackdropClick = true,
  transitionDuration = DEFAULT_TRANSITION_MS,
  children,
  className,
  container,
}) => {
  const [isMounted, setIsMounted] = useState(open);
  const [isVisible, setIsVisible] = useState(open);

  const closeTimeoutRef = useRef<number>();

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (open) {
      if (closeTimeoutRef.current) {
        window.clearTimeout(closeTimeoutRef.current);
      }

      setIsMounted(true);

      requestAnimationFrame(() => {
        setIsVisible(true);
      });
    } else {
      setIsVisible(false);

      closeTimeoutRef.current = window.setTimeout(() => {
        setIsMounted(false);
      }, transitionDuration);
    }

    return () => {
      if (closeTimeoutRef.current) {
        window.clearTimeout(closeTimeoutRef.current);
      }
    };
  }, [open, transitionDuration]);

  const handleBackdropClick = useCallback(() => {
    if (!closeOnBackdropClick) {
      return;
    }

    handleClose();
  }, [closeOnBackdropClick, handleClose]);

  const renderedChildren = useMemo(() => {
    if (typeof children === "function") {
      return children({ closeDrawer: handleClose });
    }

    return children;
  }, [children, handleClose]);

  const content = !isMounted ? null : (
    <Styled.Container className={className} isInteractive={isVisible}>
      {showBackdrop && (
        <Styled.Backdrop
          type="button"
          aria-label="Fechar menu lateral"
          onClick={handleBackdropClick}
          isVisible={isVisible}
          transitionDuration={transitionDuration}
          tabIndex={-1}
        />
      )}

      <Styled.Drawer
        role="dialog"
        aria-modal="true"
        anchor={anchor}
        isVisible={isVisible}
        width={width}
        transitionDuration={transitionDuration}
      >
        <Styled.Content>{renderedChildren}</Styled.Content>
      </Styled.Drawer>
    </Styled.Container>
  );

  const target =
    container || (typeof document !== "undefined" ? document.body : null);

  if (target) {
    return createPortal(content, target);
  }

  return content;
};

export default DrawerMenu;
